'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { trackSignUp } from '@/lib/analytics';

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  plan: string;
  terms: boolean;
}

const PLANS = [
  { id: 'plan-free', value: 'free', label: 'Free', credits: '25 credits/mo', price: '$0' },
  { id: 'plan-starter', value: 'starter', label: 'Starter', credits: '500 credits/mo', price: '$29' },
  { id: 'plan-pro', value: 'pro', label: 'Pro', credits: '5,000 credits/mo', price: '$99', recommended: true },
];

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignupFormData>({ defaultValues: { plan: 'pro' } });

  const selectedPlan = watch('plan');

  const onSubmit = async (data: SignupFormData) => {
    setLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        plan: data.plan,
      });
      trackSignUp('email');
      toast.success('Account created! Welcome to ContactSense.');
      router.push('/');
      router.refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-2xl font-800 text-foreground">Create your account</h2>
        <p className="text-sm text-muted-foreground mt-1">Start discovering verified business contacts today</p>
      </div>

      <div className="flex flex-col gap-2">
        <button className="btn-secondary w-full justify-center text-sm">
          <Icon name="GlobeAltIcon" size={16} />
          Sign up with Google
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">or with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Full name</label>
          <input
            type="text"
            className={`input-base ${errors.fullName ? 'input-error' : ''}`}
            placeholder="Your full name"
            {...register('fullName', { required: 'Full name is required' })}
          />
          {errors.fullName && <p className="text-xs text-danger mt-1">{errors.fullName.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Work email</label>
          <input
            type="email"
            className={`input-base ${errors.email ? 'input-error' : ''}`}
            placeholder="you@company.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid work email' },
            })}
          />
          {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Password</label>
          <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with at least one number</p>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`input-base pr-10 ${errors.password ? 'input-error' : ''}`}
              placeholder="Create a strong password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && <p className="text-xs text-danger mt-1">{errors.password.message}</p>}
        </div>

        {/* Plan selector */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-2">Choose your plan</label>
          <div className="grid grid-cols-3 gap-2">
            {PLANS.map((plan) => (
              <button
                key={plan.id}
                type="button"
                onClick={() => setValue('plan', plan.value)}
                className={`relative p-3 rounded-xl border-2 text-left transition-all duration-150
                  ${selectedPlan === plan.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
              >
                {plan.recommended && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-700 px-2 py-0.5 rounded-full gradient-primary text-white whitespace-nowrap">
                    Popular
                  </span>
                )}
                <p className="text-sm font-700 text-foreground">{plan.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{plan.credits}</p>
                <p className="text-xs font-700 text-primary mt-1">{plan.price}/mo</p>
              </button>
            ))}
          </div>
        </div>

        {/* Terms */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register('terms', { required: 'You must accept the terms to continue' })}
            className="w-3.5 h-3.5 rounded accent-primary mt-0.5 flex-shrink-0"
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            I agree to the{' '}
            <a href="#" className="text-primary hover:underline">Terms of Service</a>{' '}
            and{' '}
            <a href="#" className="text-primary hover:underline">Privacy Policy</a>.
            ContactSense only surfaces publicly available business contact information.
          </span>
        </label>
        {errors.terms && <p className="text-xs text-danger -mt-2">{errors.terms.message}</p>}

        <button type="submit" disabled={loading} className="btn-primary w-full justify-center text-sm py-2.5">
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={15} className="animate-spin" />
              Creating account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      <p className="text-xs text-center text-muted-foreground">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-600 hover:underline">Sign in</button>
      </p>
    </div>
  );
}